import type {
  GeocodingFeature,
  SearchBoxFeatureSuggestion,
} from "@mapbox/search-js-core";
import type {
  SearchHookDivision,
  SearchHookDivisionWithCode,
  SearchHookLocation,
} from "../../types";

export function makeDivision<T extends { name: string }>(
  prop: T | undefined,
): SearchHookDivision | undefined {
  if (!prop) {
    return undefined;
  }

  return {
    name: prop.name,
  };
}

export function makeDivisionWithCode<K extends string>(
  prop: ({ name: string } & Partial<Record<K, string>>) | undefined,
  key: K,
): SearchHookDivisionWithCode | undefined {
  if (!prop) {
    return undefined;
  }

  return {
    name: prop.name,
    code: prop[key],
  };
}

export function suggestedFeatureToLocation(
  feature: SearchBoxFeatureSuggestion,
): SearchHookLocation {
  return {
    name: feature.properties.name_preferred ?? feature.properties.name,
    formattedLocation: feature.properties.place_formatted,
    address: feature.properties.full_address,
    language: feature.properties.language,

    context: {
      address: makeDivision(feature.properties.context.address),
      addressNumber: makeDivision(feature.properties.context.address_number),
      country: makeDivisionWithCode(
        feature.properties.context.country,
        "country_code",
      ),
      district: makeDivision(feature.properties.context.district),
      locality: makeDivision(feature.properties.context.locality),
      neighborhood: makeDivision(feature.properties.context.neighborhood),
      place: makeDivision(feature.properties.context.place),
      postcode: makeDivision(feature.properties.context.postcode),
      region: makeDivisionWithCode(
        feature.properties.context.region,
        "region_code",
      ),
      street: makeDivision(feature.properties.context.street),
    },

    coords: {
      lat: feature.properties.coordinates.latitude,
      lng: feature.properties.coordinates.longitude,
    },

    bbox: feature.properties.bbox,
  };
}

export function geocodingFeatureToLocation(
  feature: GeocodingFeature,
): SearchHookLocation {
  const [minLong, minLat, maxLon, maxLat] =
    (feature.properties.bbox as [number, number, number, number] | undefined) ??
    [];

  return {
    name: feature.properties.name_preferred ?? feature.properties.name,
    formattedLocation: feature.properties.place_formatted,
    address: feature.properties.full_address,

    context: {
      address: makeDivision(feature.properties.context.address),
      country: makeDivisionWithCode(
        feature.properties.context.country,
        "country_code",
      ),
      postcode:
        makeDivision(feature.properties.context.postcode) ??
        (feature.properties.feature_type === "postcode"
          ? { name: feature.properties.name }
          : undefined),
      district: makeDivision(feature.properties.context.district),
      locality: makeDivision(feature.properties.context.locality),
      place: makeDivision(feature.properties.context.place),
      region: makeDivisionWithCode(
        feature.properties.context.region,
        "region_code",
      ),
      street: makeDivision(feature.properties.context.street),
    },

    coords: {
      lat: feature.properties.coordinates.latitude,
      lng: feature.properties.coordinates.longitude,
    },

    bbox:
      minLong && minLat && maxLon && maxLat
        ? [minLong, minLat, maxLon, maxLat]
        : undefined,
  };
}
